import { forwardRef, MenuItem, RecursivePseudo, StyleProps } from '@chakra-ui/react';
import { memo, PropsWithChildren, ReactElement } from 'react';

import { CheckOutline } from '@bonusly/ds-icon';

import { useDropdownContext } from '../lib/context';

export interface DropdownOptionProps extends StyleProps, RecursivePseudo<any>, PropsWithChildren {
	checkIcon?: ReactElement;
	compareIsSelected?: (selected: any, optionValue: any) => boolean;
	value: any;
}

// Hoisted so the default-icon reference is stable across renders. The previous
// inline default `checkIcon = <CheckOutline ... />` created a new ReactElement
// for every option on every render, defeating shallow-prop comparison.
const DEFAULT_CHECK_ICON = <CheckOutline color="text.interactive" size="md" />;

const DropdownOptionImpl = forwardRef<DropdownOptionProps, 'button'>(
	({ children, checkIcon = DEFAULT_CHECK_ICON, compareIsSelected, value: optionValue, ...rest }, ref) => {
		const { onChange, value, valueToString } = useDropdownContext();

		const isSelected = compareIsSelected ? compareIsSelected(value, optionValue) : value === optionValue;

		return (
			<MenuItem
				columnGap={2}
				display="flex"
				justifyContent="space-between"
				onClick={() => onChange(optionValue)}
				ref={ref}
				_focus={{
					bg: 'secondary.bgAlpha',
				}}
				_notLast={{
					marginBottom: '1px',
				}}
				_hover={{
					bg: 'secondary.bgAlpha',
				}}
				_selected={{
					bg: 'primary.bgAlpha',
				}}
				_focusVisible={{
					borderColor: 'border.focus',
					borderWidth: '2px',
					borderStyle: 'solid',
					outline: 'none',
					outlineOffset: '0',
				}}
				{...(isSelected && { 'data-selected': true })}
				{...rest}
			>
				{children ?? (valueToString ? valueToString(optionValue) : optionValue)}
				{isSelected && checkIcon}
			</MenuItem>
		);
	}
);

DropdownOptionImpl.displayName = 'DropdownOption';

// Wrapped in React.memo so a parent re-render does not call this component for every option.
// React's default shallow comparison covers `value`, `children`, `compareIsSelected`, `checkIcon`,
// and any pass-through style props. Context-driven re-renders (e.g. selecting a different value)
// still flow through normally because `useDropdownContext` subscribes to the provider.
export const DropdownOption = memo(DropdownOptionImpl);
